import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatDate, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const LEVEL_LABELS = {
  n0: "Prevalidación (N0)",
  n1: "Diagnóstico (N1)",
  n2: "Kit Documental (N2)",
  diag_light: "Diagnóstico ligero",
  email: "Email IA",
};

function formatUSD(n) {
  if (n == null) return "—";
  return `$${Number(n).toFixed(4)}`;
}

function formatMs(n) {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}s`;
  return `${Math.round(n)}ms`;
}

export default function AdminAIRunsPage() {
  const { runId } = useParams();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCase, setFilterCase] = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [detail, setDetail] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCase) params.append("case_id", filterCase);
      if (filterLevel) params.append("level", filterLevel);
      if (filterModel) params.append("model", filterModel);
      params.append("limit", "50");
      const { data } = await api.get(`/admin/ai-runs?${params}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar runs");
    } finally {
      setLoading(false);
    }
  }, [filterCase, filterLevel, filterModel]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/ai-runs/stats`);
      setStats(data);
    } catch (e) {
      // stats opcionales, no bloquea la página
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const loadDetail = useCallback(async () => {
    if (!runId) return;
    try {
      const { data } = await api.get(`/admin/ai-runs/${runId}`);
      setDetail(data);
    } catch (e) {
      toast.error("Run no encontrado");
    }
  }, [runId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const exportRun = async (id) => {
    try {
      const { data } = await api.get(`/admin/ai-runs/${id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-run-${id.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  return (
    <div className="container-prose py-10" data-testid="admin-ai-runs-page">
      <div className="mb-6">
        <Link to="/admin" className="text-sm text-gray-500 hover:text-primary">← Admin</Link>
        <h1 className="font-serif text-3xl text-gray-900 mt-2">Auditoría de IA — ai_runs</h1>
        <p className="text-sm text-gray-600 mt-1">
          Registro exhaustivo de cada generación LLM en la plataforma (input + raw + validated + costs + warnings + guardrail).
        </p>
      </div>

      {/* STATS */}
      {stats && (
        <div className="border border-gray-200 bg-white p-5 mb-6" data-testid="ai-runs-stats">
          <h2 className="font-serif text-lg mb-3">Resumen ({stats.total_runs} runs)</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left">Modelo</th>
                  <th className="px-3 py-2 text-left">Nivel</th>
                  <th className="px-3 py-2 text-right">Runs</th>
                  <th className="px-3 py-2 text-right">Lat avg</th>
                  <th className="px-3 py-2 text-right">Tokens in/out</th>
                  <th className="px-3 py-2 text-right">Costo USD</th>
                  <th className="px-3 py-2 text-right">Errores</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_model_level.map((s, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-3 py-2 font-mono text-xs">{s.model || "—"}</td>
                    <td className="px-3 py-2 text-xs">{LEVEL_LABELS[s.level] || s.level}</td>
                    <td className="px-3 py-2 text-right">{s.count}</td>
                    <td className="px-3 py-2 text-right text-xs">{formatMs(s.avg_latency_ms)}</td>
                    <td className="px-3 py-2 text-right text-xs">{s.total_tokens_in.toLocaleString()} / {s.total_tokens_out.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatUSD(s.total_cost_usd)}</td>
                    <td className="px-3 py-2 text-right text-xs">{s.errors > 0 ? <span className="text-rose-600 font-semibold">{s.errors}</span> : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FILTROS */}
      <div className="border border-gray-200 bg-white p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-500">Case ID</label>
          <Input value={filterCase} onChange={(e) => setFilterCase(e.target.value)} placeholder="opcional" className="mt-1" data-testid="filter-case" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-500">Nivel</label>
          <select className="mt-1 w-full border border-gray-300 rounded-sm h-10 px-2 text-sm" value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)} data-testid="filter-level">
            <option value="">Todos</option>
            <option value="n0">N0</option>
            <option value="n1">N1</option>
            <option value="n2">N2</option>
            <option value="email">Email</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-500">Modelo</label>
          <Input value={filterModel} onChange={(e) => setFilterModel(e.target.value)} placeholder="ej: claude-sonnet-4.5" className="mt-1" data-testid="filter-model" />
        </div>
        <div className="flex items-end">
          <Button onClick={loadList} disabled={loading} className="w-full" data-testid="apply-filters-btn">
            {loading ? "Cargando…" : "Aplicar"}
          </Button>
        </div>
      </div>

      {/* LIST */}
      <div className="border border-gray-200 bg-white" data-testid="ai-runs-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Nivel</th>
                <th className="px-3 py-2 text-left">Modelo</th>
                <th className="px-3 py-2 text-left">Caso</th>
                <th className="px-3 py-2 text-right">Lat</th>
                <th className="px-3 py-2 text-right">Tokens</th>
                <th className="px-3 py-2 text-right">USD</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">Sin resultados</td></tr>
              )}
              {items.map((r) => (
                <tr key={r.id} data-testid={`ai-run-${r.id}`} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-xs">{LEVEL_LABELS[r.level] || r.level}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.model_used || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono">
                    {r.case_id ? (
                      <Link to={`/admin/cases/${r.case_id}`} className="text-primary hover:underline">{r.case_id.slice(0, 8)}…</Link>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-xs">{formatMs(r.latency_ms)}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    {r.tokens_in != null ? `${r.tokens_in}/${r.tokens_out}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{formatUSD(r.cost_usd)}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.error
                      ? <span className="text-rose-600 font-semibold">error</span>
                      : <span className="text-emerald-700">ok</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/admin/ai-runs/${r.id}`} className="text-xs text-primary hover:underline mr-3" data-testid={`view-run-${r.id}`}>Ver</Link>
                    <button onClick={() => exportRun(r.id)} className="text-xs text-gray-500 hover:text-primary" data-testid={`export-run-${r.id}`}>JSON</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* DETAIL inline (cuando :runId) */}
      {detail && runId && (
        <div className="mt-8 border border-gray-200 bg-white p-6" data-testid="ai-run-detail">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl">Detalle del run</h2>
            <Link to="/admin/ai-runs" className="text-xs text-gray-500 hover:text-primary">← Volver al listado</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4">
            <Field label="Service" value={detail.service} mono />
            <Field label="Modelo" value={detail.model_used} mono />
            <Field label="Provider" value={detail.provider} />
            <Field label="Sesión" value={detail.session_id} mono small />
            <Field label="Lat. total" value={formatMs(detail.latency_ms)} />
            <Field label="Tokens in/out" value={detail.tokens_in != null ? `${detail.tokens_in} / ${detail.tokens_out}` : "—"} />
            <Field label="Costo USD" value={formatUSD(detail.cost_usd)} mono />
            <Field label="Reintentos" value={detail.retries} />
            <Field label="Artifact" value={detail.artifact_id} mono small />
            <Field label="A/B test" value={detail.ab_test_id || "—"} mono small />
            <Field label="A/B variant" value={detail.ab_variant || "—"} mono />
            <Field label="Caso" value={detail.case_id} mono small />
          </div>

          {detail.error && (
            <div className="border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900 mb-4" data-testid="ai-run-error">
              <strong>Error:</strong> {detail.error}
            </div>
          )}

          {detail.validation_warnings && detail.validation_warnings.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 mb-4" data-testid="ai-run-warnings">
              <strong>Warnings ({detail.validation_warnings.length}):</strong>
              <ul className="mt-1 space-y-0.5">
                {detail.validation_warnings.slice(0, 20).map((w, i) => <li key={i}>• {w}</li>)}
              </ul>
            </div>
          )}

          <Section title="Input payload (sanitizado)" data={detail.input_payload} testid="input-payload" />
          <Section title="Validated output" data={detail.validated_output} testid="validated-output" />
          <Section title="Raw output (LLM)" data={detail.raw_output} testid="raw-output" raw />
          {detail.attempts && detail.attempts.length > 0 && (
            <Section title="Intentos" data={detail.attempts} testid="attempts" />
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono, small }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className={`${mono ? 'font-mono' : ''} ${small ? 'text-[10px] break-all' : 'text-sm'} text-gray-900`}>{value ?? "—"}</p>
    </div>
  );
}

function Section({ title, data, testid, raw = false }) {
  const [open, setOpen] = useState(false);
  if (data == null) return null;
  return (
    <div className="border border-gray-200 mb-3" data-testid={`section-${testid}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2 text-left bg-gray-50 flex items-center justify-between hover:bg-gray-100 text-sm font-medium"
        data-testid={`section-toggle-${testid}`}
      >
        <span>{title}</span>
        <span className="text-xs text-gray-500">{open ? "▼" : "▶"}</span>
      </button>
      {open && (
        <pre className="p-3 text-[11px] font-mono bg-white overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-words">
          {raw ? (typeof data === "string" ? data : JSON.stringify(data, null, 2)) : JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
